import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { AutoState, ManualState, State, ValidationState } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'business-info',

    initial: State.INIT,

    // TODO(LED-1798): investigate why events are propagated to sibling states
    // on: {
    //   '*': {
    //     actions: shared.actions.unhandled(),
    //   },
    // },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.HasStoredCompanyInput,
            target: State.RESUME_ONBOARDING,
          },
          {
            target: State.SEARCH,
          },
        ],
      },

      [State.RESUME_ONBOARDING]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: State.SEARCH,

          RESTART_ONBOARDING: State.SEARCH,

          RESUME_ONBOARDING: {
            actions: Action.RestoreCompanyInfo,
            target: State.COMPANY_INFO_RESTORED,
          },
        },
      },

      [State.COMPANY_INFO_RESTORED]: {
        always: [
          {
            cond: Guard.IsReadyForValidation,
            target: State.VALIDATION,
          },
          {
            cond: Guard.HasSelectedExternalCompany,
            target: State.AUTO,
          },
          {
            target: State.MANUAL,
          },
        ],
      },

      [State.SEARCH]: {
        entry: [Action.ResetCompanyInfo, shared.actions.trackView()],

        on: {
          GO_CAPITAL_DEPOSIT: {
            target: State.ABORT_TO_CAPITAL_DEPOSIT,
          },

          PREV: {
            target: State.ABORT,
          },

          SKIP_EXTERNAL_COMPANY: {
            actions: Action.SkipExternalCompany,
            target: State.MANUAL,
          },

          SUBMIT_EXTERNAL_COMPANY: {
            actions: Action.SaveExternalCompanyInfo,
            target: State.AUTO,
          },
        },
      },

      [State.MANUAL]: {
        initial: ManualState.INIT,

        on: {
          PREV: State.SEARCH,
        },

        onDone: State.VALIDATION,

        states: {
          [ManualState.INIT]: {
            always: [
              {
                cond: Guard.IsLegalFormMissing,
                target: ManualState.LEGAL_FORM_QUESTION,
              },
              {
                cond: Guard.IsIdentityInfoMissing,
                target: ManualState.IDENTITY_INFO,
              },
              {
                cond: Guard.IsLegalFormMissing,
                target: ManualState.LEGAL_INFO,
              },
              {
                target: ManualState.LEGAL_INFO,
              },
            ],
          },

          [ManualState.LEGAL_FORM_QUESTION]: {
            entry: shared.actions.trackView(),

            on: {
              SUBMIT_BUSINESS_FORM: {
                actions: Action.SaveBusinessForm,
                target: ManualState.IDENTITY_INFO,
              },
            },
          },

          [ManualState.IDENTITY_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: ManualState.LEGAL_FORM_QUESTION,

              SUBMIT_IDENTITY_INFO: {
                actions: Action.SaveIdentityInfo,
                target: ManualState.LEGAL_INFO,
              },
            },
          },

          [ManualState.LEGAL_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: ManualState.IDENTITY_INFO,

              SUBMIT_LEGAL_INFO: {
                actions: Action.SaveLegalInfo,
                target: ManualState.DONE,
              },
            },
          },

          [ManualState.DONE]: {
            type: 'final',
          },
        },
      },

      [State.AUTO]: {
        initial: AutoState.INIT,

        on: {
          PREV: State.SEARCH,
        },

        onDone: State.VALIDATION,

        states: {
          [AutoState.INIT]: {
            always: [
              {
                cond: Guard.ShouldClarifyIsMicro,
                target: AutoState.IS_MICRO,
              },
              {
                cond: Guard.IsIdentityInfoMissing,
                target: AutoState.IDENTITY_INFO,
              },
              {
                cond: Guard.IsLegalInfoMissing,
                target: AutoState.LEGAL_INFO,
              },
              {
                target: AutoState.DONE,
              },
            ],
          },

          [AutoState.IS_MICRO]: {
            entry: shared.actions.trackView(),

            on: {
              SUBMIT_IS_MICRO: [
                {
                  actions: Action.SaveIsMicro,
                  cond: Guard.IsIdentityInfoMissing,
                  target: AutoState.IDENTITY_INFO,
                },
                {
                  actions: Action.SaveIsMicro,
                  cond: Guard.IsLegalInfoMissing,
                  target: AutoState.LEGAL_INFO,
                },
                {
                  actions: Action.SaveIsMicro,
                  target: AutoState.DONE,
                },
              ],
            },
          },

          [AutoState.IDENTITY_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              SUBMIT_IDENTITY_INFO: [
                {
                  actions: Action.SaveIdentityInfo,
                  cond: Guard.IsLegalInfoMissing,
                  target: AutoState.LEGAL_INFO,
                },
                {
                  actions: Action.SaveIdentityInfo,
                  target: AutoState.DONE,
                },
              ],
            },
          },

          [AutoState.LEGAL_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: AutoState.IDENTITY_INFO,

              SUBMIT_LEGAL_INFO: {
                actions: Action.SaveLegalInfo,
                target: AutoState.DONE,
              },
            },
          },

          [AutoState.DONE]: {
            type: 'final',
          },
        },
      },

      [State.VALIDATION]: {
        initial: ValidationState.INIT,

        on: {
          PREV: [
            {
              cond: Guard.HasSelectedExternalCompany,
              // We can't go back to the AUTO state because it can potentially be
              // initialized to DONE.
              target: State.SEARCH,
            },
            {
              target: State.MANUAL,
            },
          ],
        },

        onDone: State.SUBMISSION,

        states: {
          [ValidationState.INIT]: {
            always: ValidationState.INFO_VERIFICATION,
          },

          [ValidationState.INFO_VERIFICATION]: {
            entry: shared.actions.trackView(),

            on: {
              EDIT_IDENTITY_INFO: ValidationState.EDIT_IDENTITY_INFO,
              EDIT_LEGAL_INFO: ValidationState.EDIT_LEGAL_INFO,
              VALIDATE_INFORMATION: [
                {
                  cond: Guard.ApeCodeNeedsDetails,
                  target: ValidationState.APE_DETAILS,
                },
                {
                  target: ValidationState.DONE,
                },
              ],
            },
          },

          [ValidationState.EDIT_IDENTITY_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: ValidationState.INFO_VERIFICATION,

              SUBMIT_IDENTITY_INFO: {
                actions: Action.SaveIdentityInfo,
                target: ValidationState.INFO_VERIFICATION,
              },
            },
          },

          [ValidationState.EDIT_LEGAL_INFO]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: ValidationState.INFO_VERIFICATION,

              SUBMIT_LEGAL_INFO: {
                actions: Action.SaveLegalInfo,
                target: ValidationState.INFO_VERIFICATION,
              },
            },
          },

          [ValidationState.APE_DETAILS]: {
            entry: shared.actions.trackView(),

            on: {
              PREV: ValidationState.INFO_VERIFICATION,
              SUBMIT_APE_DETAILS: {
                actions: Action.SaveActivityDetails,
                target: ValidationState.DONE,
              },
            },
          },

          [ValidationState.DONE]: {
            type: 'final',
          },
        },
      },

      [State.SUBMISSION]: {
        invoke: {
          onDone: State.DONE,
          onError: [
            {
              cond: Guard.HasCompanyNotSupportedError,
              target: State.COMPANY_INELIGIBLE,
            },
            {
              target: State.CONTACT_SUPPORT,
            },
          ],
          src: Service.SubmitCompanyInfo,
        },
      },

      [State.COMPANY_INELIGIBLE]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.ABORT,
          PREV: State.VALIDATION,
        },
      },

      [State.CONTACT_SUPPORT]: {
        entry: shared.actions.trackView(),

        on: {
          PREV: [
            /**
             * This is a hotfix for that important Sentry issue: WEB-APP-93C
             * When the company creation errors on graphql, the company is still created, but not persisted in the client,
             * meaning nothing can work anymore until a hard refresh
             * (a better fix would be to update the client cache, but setting a company unmounts the onboarding and that comes with others problems)
             */
            {
              actions: [
                Action.ResetCompanyInfo,
                () => window.location.reload(),
              ],
              cond: ({ getModuleData }) =>
                !getModuleData().companyContext.companyProfileId,
            },
            { target: State.VALIDATION },
          ],
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.ABORT_TO_CAPITAL_DEPOSIT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
